import React, { useContext } from "react";
import { differenceInCalendarYears, getYear } from "date-fns";

import PropTypes from "prop-types";
import SessionContext from "app/contexts/SessionContext";
import { useDateFormatter } from "@react-aria/i18n";
import useIncompleteTimesheetApi from "features/timesheet/incomplete-timesheet/useIncompleteTimesheet";
import {
    StyledHStack,
    StyledTertiaryButton,
    StyledYearText,
    YearMonthVStack,
    WrapperVStack,
} from "./DatePickerDisplayMonthAndYear.styles";

const DatePickerDisplayMonthAndYear = ({
    state,
    setShowMonthAndYear,
    isWeekNavigator = false,
}) => {
    let months = [];
    let years = [];
    let maxYearDate = new Date();

    let dateInit = new Date();
    let minYearDate = isWeekNavigator
        ? dateInit.setFullYear(dateInit.getFullYear() - 1)
        : new Date(1995, 1, 1, 19, 0, 52);

    const session = useContext(SessionContext);
    const { useIncompleteTimesheet } = useIncompleteTimesheetApi();
    const incompleteTimesheetData = useIncompleteTimesheet(session.legacyId);

    const getIncompleteTimesheetDate = () => {
        let incompleteTimesheetsDays = [];
        if (!incompleteTimesheetData?.isLoading) {
            let stopDate = new Date();
            let firstDayOfCurrentMonth = new Date(
                stopDate.getFullYear(),
                stopDate.getMonth(),
                1
            );
            let minDate = new Date(
                firstDayOfCurrentMonth.setMonth(
                    firstDayOfCurrentMonth.getMonth() - 2
                )
            );
            const incompleteDays = incompleteTimesheetData?.data.map((x) =>
                new Date(x.date).getTime()
            );
            const stopTime = stopDate.getTime();
            while (minDate.getTime() <= stopTime) {
                let daysToAdd = 1;
                const currentTime = minDate.getTime();
                if (incompleteDays.includes(currentTime)) {
                    daysToAdd = 7;
                } else {
                    let newMinDate = new Date(minDate);
                    incompleteTimesheetsDays.push(
                        newMinDate.setFullYear(newMinDate.getFullYear())
                    );
                }
                minDate = new Date(minDate.setDate(minDate.getDate() + daysToAdd));
            }
        }
        return incompleteTimesheetsDays;
    };

    const getIncompleteTimesheetYear = () => {
        const minYearIncompleteTimesheets =
            getIncompleteTimesheetDate().reduce((a, b) => Math.min(a, b), Infinity);
        return minYearIncompleteTimesheets;
    };

    if (isWeekNavigator) {
        const incompleteTimesheetYear = getIncompleteTimesheetYear();
        if (minYearDate > incompleteTimesheetYear) {
            minYearDate = incompleteTimesheetYear;
        }
    }

    const formatter = useDateFormatter({
        month: "short",
        timeZone: state.timeZone,
    });

    const numMonths = state.focusedDate.calendar.getMonthsInYear(
        state.focusedDate
    );

    const updateYearArray = (i) => {
        let date = state.focusedDate.add({ years: i });

        years.push({
            value: date,
            formatted: formatter.format(date.toDate(state.timeZone)),
        });
    };

    if (
        getYear(new Date(state.focusedDate)) <= getYear(maxYearDate) &&
        getYear(new Date(state.focusedDate)) >= getYear(minYearDate)
    ) {
        let limit =
            differenceInCalendarYears(minYearDate, maxYearDate) +
            differenceInCalendarYears(maxYearDate, new Date(state.focusedDate));

        for (
            let i = differenceInCalendarYears(
                maxYearDate,
                new Date(state.focusedDate)
            );
            i >= limit;
            i--
        ) {
            updateYearArray(i);
        }
    } else if (getYear(new Date(state.focusedDate)) > getYear(maxYearDate)) {
        for (
            let i = 0;
            i >=
            differenceInCalendarYears(minYearDate, new Date(state.focusedDate));
            i--
        ) {
            updateYearArray(i);
        }
    } else if (getYear(new Date(state.focusedDate)) < getYear(minYearDate)) {
        for (
            let i = differenceInCalendarYears(
                maxYearDate,
                new Date(state.focusedDate)
            );
            i >= 0;
            i--
        ) {
            updateYearArray(i);
        }
    }

    for (let i = 1; i <= numMonths; i++) {
        let date = state.focusedDate.set({ month: i });
        months.push(formatter.format(date.toDate(state.timeZone)));
    }

    const onChange = (monthVal, yearVal) => {
        const date = state.focusedDate.set({
            month: Number(monthVal),
            year: Number(yearVal),
        });
        state.setFocusedDate(date);
        setShowMonthAndYear(false);
    };

    return (
        <WrapperVStack>
            {years.map((year) => (
                <YearMonthVStack key={year.value.year}>
                    <StyledYearText
                        variant="Descriptive_1_1"
                        color="Gray_3"
                    >
                        {year.value.year}
                    </StyledYearText>

                    <StyledHStack justify="space-between">
                        {months.map((month, i) => (
                            <StyledTertiaryButton
                                key={month}
                                isSelected={
                                    state?.focusedDate?.month === i + 1 &&
                                    state?.focusedDate?.year === year.value.year
                                }
                                isCurrent={
                                    state?.value?.month === i + 1 &&
                                    state?.value?.year === year.value.year
                                }
                                onClick={() => onChange(i + 1, year.value.year)}
                            >
                                {month}
                            </StyledTertiaryButton>
                        ))}
                    </StyledHStack>
                </YearMonthVStack>
            ))}
        </WrapperVStack>
    );
};

DatePickerDisplayMonthAndYear.propTypes = {
    state: PropTypes.object.isRequired,
    setShowMonthAndYear: PropTypes.func.isRequired,
    isWeekNavigator: PropTypes.bool,
};

export default DatePickerDisplayMonthAndYear;
